import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import EmojiPicker from 'emoji-picker-react';
import UserContextMenu from './UserContextMenu';
import UserProfile from './UserProfile';
import { useNotification } from './NotificationSystem';
import { API_URL, TENOR_API_KEY, TENOR_CLIENT_KEY, COLORS } from '../config';
import './ChatArea.css';

const URL_REGEX = /(https?:\/\/[^\s]+)/g;

function ChatArea({ channel, messages, onSendMessage, user, socket, members = [], servers = [], currentServer = null, token, onCreateDM, friends = [], userPermissions = {}, onToggleMobileServerList, onToggleMobileChannelList, onToggleMobileMemberList }) {
  const { showSuccess, showError, showInfo, confirm } = useNotification();
  const [messageInput, setMessageInput] = useState('');
  const [linkPreviews, setLinkPreviews] = useState({});
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [gifSearchQuery, setGifSearchQuery] = useState('');
  const [gifs, setGifs] = useState([]);
  const [loadingGifs, setLoadingGifs] = useState(false);
  const [hoveredMessage, setHoveredMessage] = useState(null);
  const [messageReactions, setMessageReactions] = useState({});
  const [editingMessage, setEditingMessage] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageModal, setImageModal] = useState(null);
  const [mentionQuery, setMentionQuery] = useState('');
  const [showMentionAutocomplete, setShowMentionAutocomplete] = useState(false);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const [serverRoles, setServerRoles] = useState([]);
  const [contextMenu, setContextMenu] = useState(null);
  const [profileModal, setProfileModal] = useState(null);
  const messagesEndRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const gifPickerRef = useRef(null);
  const editInputRef = useRef(null);
  const messageInputRef = useRef(null);
  const mentionAutocompleteRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Get role styling for a user
  const getRoleStyle = (roles) => {
    if (!roles || roles.length === 0) return {};
    
    // Sort by position (highest first) and find first role with custom color (not default colors)
    const defaultColors = [COLORS.PRIMARY, COLORS.ROLE_DEFAULT, COLORS.ROLE_MEMBER];
    const sortedRoles = [...roles].sort((a, b) => (b.position || 0) - (a.position || 0));
    const highestRole = sortedRoles.find(role => role.color && !defaultColors.includes(role.color.toLowerCase()));
    
    if (!highestRole) return {};
    
    return {
      color: highestRole.color || COLORS.PRIMARY,
      gradient: highestRole.gradient || null,
      holographic: highestRole.holographic || false
    };
  };

  // Render username with role styling
  const renderUsername = (message) => {
    const roleStyle = getRoleStyle(message.roles);
    const classNames = ['message-username'];
    const style = {};
    
    if (roleStyle.holographic) {
      classNames.push('holographic');
    }
    
    if (roleStyle.gradient) {
      classNames.push('gradient-text');
      style['--role-gradient'] = roleStyle.gradient;
    } else if (roleStyle.color) {
      style.color = roleStyle.color;
    }
    
    return (
      <span 
        className={classNames.join(' ')} 
        style={style}
        onClick={(e) => handleUsernameClick(message, e)}
        onContextMenu={(e) => handleUsernameRightClick(message, e)}
      >
        {message.username}
      </span>
    );
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Reset reactions when channel changes
  useEffect(() => {
    setMessageReactions({});
    setLinkPreviews({});
  }, [channel?.id]);

  // Fetch server roles when server changes
  useEffect(() => {
    if (currentServer?.id && token) {
      fetchServerRoles();
    } else {
      setServerRoles([]);
    }
  }, [currentServer?.id, token]);

  const fetchServerRoles = async () => {
    try {
      const response = await fetch(`${API_URL}/api/servers/${currentServer.id}/roles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const roles = await response.json();
        setServerRoles(roles);
      }
    } catch (error) {
      console.error('Error fetching server roles:', error);
    }
  };

  useEffect(() => {
    // Extract URLs from messages and fetch previews
    // Also initialize reactions from messages
    messages.forEach(message => {
      const urls = message.content.match(URL_REGEX);
      if (urls && urls.length > 0 && !linkPreviews[message.id]) {
        const url = urls[0];
        // Skip link previews for direct media files and GIF providers
        const skipPreview = /\.(gif|jpg|jpeg|png|webp|mp4|webm)(\?|$)/i.test(url) ||
                           /tenor\.com|giphy\.com|tenor\.googleapis\.com/i.test(url);
        if (!skipPreview) {
          fetchLinkPreview(message.id, url);
        }
      }
      
      // Initialize reactions for new messages, preserving existing reactions
      if (message.reactions && !messageReactions[message.id]) {
        setMessageReactions(prev => ({
          ...prev,
          [message.id]: message.reactions
        }));
      }
    });
  }, [messages]);

  useEffect(() => {
    // Close emoji picker when clicking outside
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
      if (gifPickerRef.current && !gifPickerRef.current.contains(event.target)) {
        setShowGifPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    // Close image modal on ESC key
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && imageModal) {
        setImageModal(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [imageModal]);

  useEffect(() => {
    // Close image modal on ESC key
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && imageModal) {
        setImageModal(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [imageModal]);

  useEffect(() => {
    if (!socket) return;

    socket.on('message_reaction', ({ messageId, reactions }) => {
      setMessageReactions(prev => ({
        ...prev,
        [messageId]: reactions
      }));
    });

    return () => {
      socket.off('message_reaction');
    };
  }, [socket]);

  useEffect(() => {
    if (editingMessage && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editingMessage]);

  // Detect @ mentions in input
  useEffect(() => {
    const lastWord = messageInput.split(/\s/).pop();
    if (lastWord && lastWord.startsWith('@')) {
      const query = lastWord.substring(1).toLowerCase();
      setMentionQuery(query);
      setShowMentionAutocomplete(true);
      setSelectedMentionIndex(0);
    } else {
      setShowMentionAutocomplete(false);
      setMentionQuery('');
    }
  }, [messageInput]);

  // Filter members for mention autocomplete
  const specialMentions = [
    { id: 'everyone', username: 'everyone', isSpecial: true },
    { id: 'here', username: 'here', isSpecial: true }
  ];
  
  const filteredMembers = members.filter(member => 
    member.username.toLowerCase().includes(mentionQuery.toLowerCase()) &&
    member.id !== user.id
  );
  
  const filteredSpecialMentions = specialMentions.filter(special =>
    special.username.toLowerCase().includes(mentionQuery.toLowerCase())
  );
  
  const filteredRoles = serverRoles.filter(role =>
    role.name.toLowerCase().includes(mentionQuery.toLowerCase())
  ).map(role => ({
    id: role.id,
    name: role.name,
    color: role.color,
    gradient: role.gradient,
    holographic: role.holographic,
    isRole: true
  }));
  
  const filteredMentions = [...filteredSpecialMentions, ...filteredRoles, ...filteredMembers].slice(0, 8);

  const completeMention = (item) => {
    const words = messageInput.split(/\s/);
    const mentionText = item.isRole ? item.name : (item.username || item.name);
    words[words.length - 1] = `@${mentionText} `;
    setMessageInput(words.join(' '));
    setShowMentionAutocomplete(false);
    messageInputRef.current?.focus();
  };

  const fetchLinkPreview = async (messageId, url) => {
    try {
      const response = await fetch(`${API_URL}/api/link-preview?url=${encodeURIComponent(url)}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const preview = await response.json();
        setLinkPreviews(prev => ({ ...prev, [messageId]: preview }));
      }
    } catch (err) {
      console.error('Failed to fetch link preview:', err);
    }
  };

  const renderMessageContent = (content) => {
    // Check if content is just a media URL
    const mediaUrlMatch = content.trim().match(/^(https?:\/\/[^\s]+\.(gif|jpg|jpeg|png|webp|mp4|webm)(\?[^\s]*)?)$/i) ||
                         content.trim().match(/^(https?:\/\/(?:.*\.)?(?:tenor\.googleapis\.com|tenor\.com|giphy\.com)[^\s]+)$/i);
    
    if (mediaUrlMatch) {
      const url = mediaUrlMatch[1];
      const isVideo = /\.(mp4|webm)(\?|$)/i.test(url);
      
      return (
        <div className="message-media">
          {isVideo ? (
            <video 
              src={url} 
              controls 
              className="message-video"
              style={{ maxWidth: '400px', maxHeight: '300px', borderRadius: '4px' }}
            />
          ) : (
            <img 
              src={url} 
              alt="Media" 
              className="message-gif"
              onClick={() => setImageModal(url)}
              style={{ maxWidth: '400px', maxHeight: '300px', borderRadius: '4px', cursor: 'pointer' }}
            />
          )}
        </div>
      );
    }
    
    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({node, ...props}) => <p {...props} />,
          a: ({node, href, ...props}) => <a href={href} target="_blank" rel="noopener noreferrer" {...props} />,
        }}
      >
        {content}
      </ReactMarkdown>
    );
  };

  const renderContentWithMentions = (content) => {
    // Split content by mentions
    const mentionRegex = /@(\w+)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    let key = 0;
    
    while ((match = mentionRegex.exec(content)) !== null) {
      // Add text before mention
      if (match.index > lastIndex) {
        parts.push(
          <span key={`text-${key++}`}>
            {renderMessageContent(content.substring(lastIndex, match.index))}
          </span>
        );
      }
      // Add mention
      const username = match[1];
      const isEveryoneMention = username === 'everyone';
      const isHereMention = username === 'here';
      const isMentioned = username === user.username || isEveryoneMention || isHereMention;
      const mentionedUser = members.find(m => m.username === username);
      
      parts.push(
        <span 
          key={`mention-${key++}`}
          className={`mention ${isMentioned ? 'mention-me' : ''} ${isEveryoneMention || isHereMention ? 'mention-everyone' : ''}`}
          data-username={username}
          onClick={(e) => {
            e.stopPropagation();
            if (mentionedUser && !isEveryoneMention && !isHereMention) {
              setProfileModal({ id: mentionedUser.id, username: mentionedUser.username });
            }
          }}
          onContextMenu={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (mentionedUser && !isEveryoneMention && !isHereMention) {
              setContextMenu({
                user: mentionedUser,
                position: { x: e.clientX, y: e.clientY }
              });
            }
          }}
        >
          @{username}
        </span>
      );
      lastIndex = match.index + match[0].length;
    }
    // Add remaining text
    if (lastIndex < content.length) {
      parts.push(
        <span key={`text-${key++}`}>
          {renderMessageContent(content.substring(lastIndex))}
        </span>
      );
    }
    
    return parts.length > 0 ? <>{parts}</> : renderMessageContent(content);
  };

  const handleUsernameClick = (message, e) => {
    e.stopPropagation();
    setProfileModal({
      id: message.userId,
      username: message.username
    });
  };

  const handleUsernameRightClick = (message, e) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      user: { id: message.userId, username: message.username },
      position: { x: e.clientX, y: e.clientY }
    });
  };

  const handleEmojiClick = (emojiData) => {
    setMessageInput(prev => prev + emojiData.emoji);
    setShowEmojiPicker(false);
  };

  const searchGifs = async (query) => {
    if (!query.trim()) {
      setGifs([]);
      return;
    }

    setLoadingGifs(true);
    try {
      // Using Tenor API - free and no API key required for basic search
      const response = await fetch(
        `https://tenor.googleapis.com/v2/search?q=${encodeURIComponent(query)}&key=${TENOR_API_KEY}&client_key=${TENOR_CLIENT_KEY}&limit=20`
      );
      const data = await response.json();
      setGifs(data.results || []);
    } catch (error) {
      console.error('Error searching GIFs:', error);
      setGifs([]);
    } finally {
      setLoadingGifs(false);
    }
  };

  const handleGifClick = (gifUrl) => {
    if (!gifUrl) return;
    
    // Send GIF as a message
    onSendMessage(gifUrl);
    setShowGifPicker(false);
    setGifSearchQuery('');
    setGifs([]);
  };

  // Search GIFs when query changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (showGifPicker) {
        searchGifs(gifSearchQuery);
      }
    }, 500); // Debounce search

    return () => clearTimeout(timer);
  }, [gifSearchQuery, showGifPicker]);

  // Load trending GIFs when picker opens
  useEffect(() => {
    if (showGifPicker && !gifSearchQuery) {
      searchGifs('trending');
    }
  }, [showGifPicker]);

  const handleReaction = (messageId, emoji) => {
    if (socket) {
      socket.emit('add_reaction', { messageId, emoji });
    }
  };

  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.indexOf('image') !== -1) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          await uploadImage(file);
        }
        break;
      }
    }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('Upload failed');
      }
      
      const data = await response.json();
      
      // Send message with attachment
      socket.emit('send_message', {
        channelId: channel.id,
        content: messageInput.trim() || '📷 Image',
        attachment: data.url
      });
      
      // Clear input
      setMessageInput('');
    } catch (error) {
      console.error('Error uploading image:', error);
      showError('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (messageInput.trim()) {
      onSendMessage(messageInput);
      setMessageInput('');
    }
  };

  const handleKeyDown = (e) => {
    // Handle mention autocomplete navigation
    if (showMentionAutocomplete && filteredMentions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedMentionIndex(prev => 
          prev < filteredMentions.length - 1 ? prev + 1 : 0
        );
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedMentionIndex(prev => 
          prev > 0 ? prev - 1 : filteredMentions.length - 1
        );
        return;
      }
      if (e.key === 'Tab' || e.key === 'Enter') {
        if (!e.shiftKey) {
          e.preventDefault();
          completeMention(filteredMentions[selectedMentionIndex]);
          return;
        }
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowMentionAutocomplete(false);
        return;
      }
    }

    // Submit on Enter without Shift (when not autocompleting)
    if (e.key === 'Enter' && !e.shiftKey && !showMentionAutocomplete) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const startEditing = (message) => {
    setEditingMessage(message.id);
    setEditContent(message.content);
    setHoveredMessage(null);
  };

  const cancelEdit = () => {
    setEditingMessage(null);
    setEditContent('');
  };

  const saveEdit = (messageId) => {
    if (!editContent.trim() || !socket) return;
    
    socket.emit('edit_message', {
      messageId,
      content: editContent.trim()
    });
    
    cancelEdit();
  };

  const handleEditKeyDown = (e, messageId) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      saveEdit(messageId);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEdit();
    }
  };

  const canEditMessage = (message) => {
    // Can always edit your own messages if you have EDIT_MESSAGES permission
    const hasEditPerm = userPermissions.EDIT_MESSAGES || message.userId === user.id;
    return message.userId === user.id && hasEditPerm;
  };

  const canDeleteMessage = (message) => {
    // Can delete own messages, or any message if you have DELETE_MESSAGES permission
    return message.userId === user.id || userPermissions.DELETE_MESSAGES;
  };

  const canSendMessages = () => {
    // Default to true if permissions haven't loaded or are undefined
    return userPermissions.SEND_MESSAGES !== false;
  };

  const handleDeleteMessage = async (messageId) => {
    const confirmed = await confirm('Delete this message?', 'Confirm Deletion');
    if (!confirmed) return;
    
    try {
      const endpoint = channel.type === 'dm' 
        ? `${API_URL}/api/dms/${channel.id}/messages/${messageId}`
        : `${API_URL}/api/channels/${channel.id}/messages/${messageId}`;
        
      await fetch(endpoint, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Socket will handle the update
    } catch (error) {
      console.error('Error deleting message:', error);
      showError('Failed to delete message');
    }
  };

  if (!channel) {
    return (
      <div className="chat-area">
        <div className="no-channel">
          <h2>Welcome to Ripkord!</h2>
          <p>Select a channel to start chatting</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-area">
      <div className="chat-header">
        <button className="mobile-menu-btn" onClick={() => {
          onToggleMobileServerList();
          onToggleMobileChannelList();
        }} title="Menu">
          <svg width="20" height="20" viewBox="0 0 20 20">
            <path fill="currentColor" d="M3 3h14v2H3V3zm0 6h14v2H3V9zm0 6h14v2H3v-2z"/>
          </svg>
        </button>
        <span className="channel-hash">#</span>
        <span className="channel-title">{channel.name}</span>
        <button className="mobile-menu-btn members-btn" onClick={onToggleMobileMemberList} title="Members">
          <svg width="20" height="20" viewBox="0 0 20 20">
            <path fill="currentColor" d="M14 11c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm-4-4c1.66 0 3-1.34 3-3S11.66 1 10 1 7 2.34 7 4s1.34 3 3 3zm0 1c-2 0-6 1-6 3v2h12v-2c0-2-4-3-6-3zm4 1c.29 0 .62.02.97.05.02.01.03.03.03.05v2h4v-2c0-1.22-1.47-1.98-3-2.32-.65.84-1.59 1.27-2 1.27z"/>
          </svg>
        </button>
      </div>

      <div className="messages-container">
        <div className="messages-scroll">
          <div className="channel-welcome">
            <div className="channel-icon-large">#</div>
            <h3>Welcome to {channel.type === 'dm' ? '' : '#'}{channel.name}!</h3>
            <p>This is the beginning of {channel.type === 'dm' ? 'your conversation with' : 'the'} {channel.type === 'dm' ? '' : '#'}{channel.name}{channel.type === 'dm' ? '' : ' channel'}.</p>
          </div>

          {messages.map((message, index) => {
            const messageDate = new Date(message.timestamp);
            
            // Show avatar if: first message, different user, or >5 minutes since last message
            let showAvatar = index === 0 || messages[index - 1].userId !== message.userId;
            if (!showAvatar && index > 0) {
              const prevMessageDate = new Date(messages[index - 1].timestamp);
              const timeDiff = messageDate - prevMessageDate;
              const fiveMinutes = 5 * 60 * 1000; // 5 minutes in milliseconds
              showAvatar = timeDiff > fiveMinutes;
            }
            
            const today = new Date();
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);
            
            const isToday = messageDate.toDateString() === today.toDateString();
            const isYesterday = messageDate.toDateString() === yesterday.toDateString();
            
            // Smart date for messages with avatar
            let timestamp;
            if (isToday) {
              timestamp = messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            } else if (isYesterday) {
              timestamp = 'Yesterday at ' + messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            } else {
              timestamp = messageDate.toLocaleDateString() + ' ' + messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            }
            
            // Simple time-only for compact messages
            const compactTimestamp = messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            
            const fullTimestamp = messageDate.toLocaleString([], { 
              year: 'numeric', 
              month: '2-digit', 
              day: '2-digit', 
              hour: '2-digit', 
              minute: '2-digit'
            });
            const reactions = messageReactions[message.id] || {};

            return (
              <div 
                key={message.id} 
                className={`message ${showAvatar ? 'with-avatar' : 'compact'}`}
                onMouseEnter={() => setHoveredMessage(message.id)}
                onMouseLeave={() => setHoveredMessage(null)}
              >
                {showAvatar ? (
                  <>
                    <div className="message-avatar">
                      {message.avatar_url ? (
                        <img 
                          src={`${API_URL}${message.avatar_url}`} 
                          alt={message.username}
                          className="avatar-img"
                        />
                      ) : (
                        message.username.substring(0, 1).toUpperCase()
                      )}
                    </div>
                    <div className="message-content-wrapper">
                      <div className="message-header">
                        {renderUsername(message)}
                        <span className="message-timestamp">{timestamp}</span>
                        {message.editedAt && <span className="message-edited">(edited)</span>}
                      </div>
                      
                      {editingMessage === message.id ? (
                        <div className="message-edit-wrapper">
                          <textarea
                            ref={editInputRef}
                            className="message-edit-input"
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                            onKeyDown={(e) => handleEditKeyDown(e, message.id)}
                            rows={3}
                          />
                          <div className="message-edit-hint">
                            escape to <strong>cancel</strong> • enter to <strong>save</strong>
                          </div>
                        </div>
                      ) : (
                        <div className="message-text">{renderContentWithMentions(message.content)}</div>
                      )}
                      
                      {hoveredMessage === message.id && !editingMessage && (
                        <div className="message-actions">
                          {canEditMessage(message) && (
                            <button 
                              className="reaction-button edit-button" 
                              onClick={() => startEditing(message)}
                              title="Edit message"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M20.71 7.04c.39-.39.39-1.04 0-1.41l-2.34-2.34c-.37-.39-1.02-.39-1.41 0l-1.84 1.83 3.75 3.75M3 17.25V21h3.75L17.81 9.93l-3.75-3.75L3 17.25z"/>
                              </svg>
                            </button>
                          )}
                          {canDeleteMessage(message) && (
                            <button 
                              className="reaction-button delete-button" 
                              onClick={() => handleDeleteMessage(message.id)}
                              title="Delete message"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                              </svg>
                            </button>
                          )}
                          <button 
                            className="reaction-button" 
                            onClick={() => handleReaction(message.id, '👍')}
                            title="React with thumbs up"
                          >
                            👍
                          </button>
                          <button 
                            className="reaction-button" 
                            onClick={() => handleReaction(message.id, '❤️')}
                            title="React with heart"
                          >
                            ❤️
                          </button>
                          <button 
                            className="reaction-button" 
                            onClick={() => handleReaction(message.id, '😂')}
                            title="React with laughing"
                          >
                            😂
                          </button>
                          <button 
                            className="reaction-button" 
                            onClick={() => handleReaction(message.id, '🎉')}
                            title="React with party"
                          >
                            🎉
                          </button>
                        </div>
                      )}

                      {Object.keys(reactions).length > 0 && (
                        <div className="message-reactions">
                          {Object.entries(reactions).map(([emoji, reactionData]) => (
                            <button
                              key={emoji}
                              className={`reaction-item ${reactionData.users.includes(user.id) ? 'reacted' : ''}`}
                              onClick={() => handleReaction(message.id, emoji)}
                            >
                              <span className="reaction-emoji">{emoji}</span>
                              <span className="reaction-count">{reactionData.count}</span>
                            </button>
                          ))}
                        </div>
                      )}

                      {message.attachment && (
                        <div className="message-attachment">
                          <img 
                            src={`${API_URL}${message.attachment}`} 
                            alt="Uploaded image" 
                            className="message-image"
                            onClick={() => setImageModal(`${API_URL}${message.attachment}`)}
                          />
                        </div>
                      )}

                      {linkPreviews[message.id] && (
                        <div className="link-preview">
                          {linkPreviews[message.id].image && (
                            <img src={linkPreviews[message.id].image} alt="Preview" className="link-preview-image" />
                          )}
                          <div className="link-preview-content">
                            <div className="link-preview-title">{linkPreviews[message.id].title}</div>
                            {linkPreviews[message.id].description && (
                              <div className="link-preview-description">{linkPreviews[message.id].description}</div>
                            )}
                            <div className="link-preview-url">{linkPreviews[message.id].url}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="message-timestamp-compact" title={fullTimestamp}>{compactTimestamp}</div>
                    <div className="message-content-wrapper compact">
                      {editingMessage === message.id ? (
                        <div className="message-edit-wrapper">
                          <textarea
                            ref={editInputRef}
                            className="message-edit-input"
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                            onKeyDown={(e) => handleEditKeyDown(e, message.id)}
                            rows={3}
                          />
                          <div className="message-edit-hint">
                            escape to <strong>cancel</strong> • enter to <strong>save</strong>
                          </div>
                        </div>
                      ) : (
                        <div className="message-text-compact">{renderContentWithMentions(message.content)}</div>
                      )}
                      
                      {hoveredMessage === message.id && !editingMessage && (
                        <div className="message-actions compact">
                          {canEditMessage(message) && (
                            <button 
                              className="reaction-button edit-button" 
                              onClick={() => startEditing(message)}
                              title="Edit message"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M20.71 7.04c.39-.39.39-1.04 0-1.41l-2.34-2.34c-.37-.39-1.02-.39-1.41 0l-1.84 1.83 3.75 3.75M3 17.25V21h3.75L17.81 9.93l-3.75-3.75L3 17.25z"/>
                              </svg>
                            </button>
                          )}
                          {canDeleteMessage(message) && (
                            <button 
                              className="reaction-button delete-button" 
                              onClick={() => handleDeleteMessage(message.id)}
                              title="Delete message"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                              </svg>
                            </button>
                          )}
                          <button className="reaction-button" onClick={() => handleReaction(message.id, '👍')}>👍</button>
                          <button className="reaction-button" onClick={() => handleReaction(message.id, '❤️')}>❤️</button>
                          <button className="reaction-button" onClick={() => handleReaction(message.id, '😂')}>😂</button>
                          <button className="reaction-button" onClick={() => handleReaction(message.id, '🎉')}>🎉</button>
                        </div>
                      )}

                      {Object.keys(reactions).length > 0 && (
                        <div className="message-reactions">
                          {Object.entries(reactions).map(([emoji, reactionData]) => (
                            <button
                              key={emoji}
                              className={`reaction-item ${reactionData.users.includes(user.id) ? 'reacted' : ''}`}
                              onClick={() => handleReaction(message.id, emoji)}
                            >
                              <span className="reaction-emoji">{emoji}</span>
                              <span className="reaction-count">{reactionData.count}</span>
                            </button>
                          ))}
                        </div>
                      )}

                      {message.attachment && (
                        <div className="message-attachment">
                          <img 
                            src={`${API_URL}${message.attachment}`} 
                            alt="Uploaded image" 
                            className="message-image"
                            onClick={() => setImageModal(`${API_URL}${message.attachment}`)}
                          />
                        </div>
                      )}

                      {linkPreviews[message.id] && (
                        <div className="link-preview">
                        {linkPreviews[message.id].image && (
                          <img src={linkPreviews[message.id].image} alt="Preview" className="link-preview-image" />
                        )}
                        <div className="link-preview-content">
                          <div className="link-preview-title">{linkPreviews[message.id].title}</div>
                          {linkPreviews[message.id].description && (
                            <div className="link-preview-description">{linkPreviews[message.id].description}</div>
                          )}
                          <div className="link-preview-url">{linkPreviews[message.id].url}</div>
                        </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>
      </div>

      <div className="message-input-container">
        {uploadingImage && (
          <div className="upload-indicator">
            <div className="upload-spinner"></div>
            <span>Uploading image...</span>
          </div>
        )}
        
        {showMentionAutocomplete && filteredMentions.length > 0 && (
          <div className="mention-autocomplete" ref={mentionAutocompleteRef}>
            {filteredMentions.map((item, index) => {
              const isRole = item.isRole;
              const isSpecial = item.isSpecial;
              const displayName = isRole ? item.name : (item.username || item.name);
              
              return (
                <div
                  key={item.id}
                  className={`mention-item ${index === selectedMentionIndex ? 'selected' : ''} ${isSpecial ? 'mention-special' : ''} ${isRole ? 'mention-role' : ''}`}
                  onClick={() => completeMention(item)}
                  onMouseEnter={() => setSelectedMentionIndex(index)}
                >
                  <div className="mention-avatar">
                    {isSpecial ? '@' : isRole ? '🎭' : displayName[0].toUpperCase()}
                  </div>
                  <span 
                    className="mention-username"
                    style={isRole && item.color ? { color: item.color } : {}}
                  >
                    {displayName}
                    {isSpecial && <span className="mention-special-label"> - Notifies {item.username === 'everyone' ? 'all members' : 'online members'}</span>}
                    {isRole && <span className="mention-role-label"> - Role</span>}
                  </span>
                </div>
              );
            })}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="message-form">
          <div className="input-wrapper">
            <textarea
              ref={messageInputRef}
              className="message-input"
              placeholder={canSendMessages() ? `Message #${channel.name}` : 'You do not have permission to send messages in this channel'}
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              rows={1}
              disabled={uploadingImage || !canSendMessages()}
            />
            <button
              type="button"
              className="emoji-button"
              onClick={() => {
                setShowEmojiPicker(!showEmojiPicker);
                setShowGifPicker(false);
              }}
              title="Add emoji"
            >
              😊
            </button>
            <button
              type="button"
              className="gif-button"
              onClick={() => {
                setShowGifPicker(!showGifPicker);
                setShowEmojiPicker(false);
              }}
              title="Add GIF"
            >
              GIF
            </button>
          </div>
          {showEmojiPicker && (
            <div className="emoji-picker-wrapper" ref={emojiPickerRef}>
              <EmojiPicker 
                onEmojiClick={handleEmojiClick}
                theme="dark"
                searchPlaceHolder="Search emoji..."
                height={400}
                width={320}
                previewConfig={{ showPreview: false }}
              />
            </div>
          )}
          {showGifPicker && (
            <div className="gif-picker-wrapper" ref={gifPickerRef}>
              <div className="gif-picker-header">
                <input 
                  type="text" 
                  placeholder="Search GIFs..."
                  className="gif-search-input"
                  value={gifSearchQuery}
                  onChange={(e) => setGifSearchQuery(e.target.value)}
                  autoFocus
                />
                <button className="gif-close-btn" onClick={() => setShowGifPicker(false)}>×</button>
              </div>
              <div className="gif-picker-content">
                {loadingGifs ? (
                  <div className="gif-loading">Searching...</div>
                ) : gifs.length > 0 ? (
                  gifs.map((gif, index) => (
                    <div
                      key={index}
                      className="gif-item"
                      onClick={() => handleGifClick(gif.media_formats?.gif?.url || gif.url)}
                    >
                      <img 
                        src={gif.media_formats?.tinygif?.url || gif.media_formats?.gif?.url} 
                        alt={gif.content_description || 'GIF'} 
                      />
                    </div>
                  ))
                ) : (
                  <div className="gif-placeholder">
                    {gifSearchQuery ? 'No GIFs found' : 'Search for GIFs or browse trending'}
                  </div>
                )}
              </div>
            </div>
          )}
        </form>
      </div>

      {imageModal && (
        <div className="image-modal" onClick={() => setImageModal(null)}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="image-modal-close" onClick={() => setImageModal(null)}>
              ×
            </button>
            <img src={imageModal} alt="Full size" className="image-modal-img" />
          </div>
        </div>
      )}

      {contextMenu && (
        <UserContextMenu
          user={contextMenu.user}
          currentUser={user}
          position={contextMenu.position}
          onClose={() => setContextMenu(null)}
          onProfile={() => {
            setProfileModal(contextMenu.user);
            setContextMenu(null);
          }}
          onMention={() => {
            setMessageInput(prev => prev + `@${contextMenu.user.username} `);
            setContextMenu(null);
            messageInputRef.current?.focus();
          }}
          onMessage={() => {
            onCreateDM(contextMenu.user.id);
            setContextMenu(null);
          }}
          onInviteToServer={(serverId) => {
            // Invite to server functionality
            setContextMenu(null);
          }}
          onAddFriend={async () => {
            try {
              const response = await fetch(`${API_URL}/api/friends/request`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ username: contextMenu.user.username })
              });
              const data = await response.json();
              showSuccess(data.message || 'Friend request sent');
            } catch (error) {
              showError('Failed to send friend request');
            }
            setContextMenu(null);
          }}
          onBlock={async () => {
            const confirmed = await confirm(`Are you sure you want to block ${contextMenu.user.username}?`, 'Block User');
            if (confirmed) {
              try {
                await fetch(`${API_URL}/api/users/block`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  },
                  body: JSON.stringify({ userId: contextMenu.user.id })
                });
                showSuccess('User blocked');
              } catch (error) {
                showError('Failed to block user');
              }
            }
            setContextMenu(null);
          }}          servers={servers}
          friends={friends}
        />
      )}

      {profileModal && (
        <UserProfile
          user={profileModal}
          onClose={() => setProfileModal(null)}
          onMessage={(userId) => {
            onCreateDM(userId);
            setProfileModal(null);
          }}
          friends={friends}
          onAddFriend={async (userId) => {
            try {
              const response = await fetch(`${API_URL}/api/friends/request`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ username: profileModal.username })
              });
              const data = await response.json();
              showSuccess(data.message || 'Friend request sent');
            } catch (error) {
              showError('Failed to send friend request');
            }
            setProfileModal(null);
          }}
          token={token}
        />
      )}
    </div>
  );
}

export default ChatArea;
